// ═══ THE ACTIVITY LOG, READ FOR PEOPLE — Utilities → Activity and Productivity's "Work done" (Philip 2026-10-23) ═══
// activity_log holds two kinds of row:
//   • CHANGES, written by the database (log_change(), migrations 2026-10-23_activity_log_*): one per added / edited /
//     deleted row of a work table — table_name, op, staff_id (by login), source, client / property / parcel / year,
//     and changes ({ column: [old, new] } on an edit; the row's columns on an add or delete).
//   • EVENTS, written by a screen (scan, file, mark paid, …): action, entity_type, entity_name, details. Since stage (c)
//     they carry staff_id and source too; older ones only the staff_user name.
// Pure: grouping, labels and counts. The screens do the fetching.

// Kinds of work — the Activity filter and the Work done columns. A change belongs to its table's kind; an event to its
// entity_type's.
export const KINDS = [
  { key: 'values', label: 'Values', tables: ['parcel_appeals', 'parcel_appeal_deductions', 'parcel_values', 'parcel_prorata', 'parcel_jurisdiction_rates', 'parcel_status', 'parcel_year_data'] },
  { key: 'notices', label: 'Notices & decisions', tables: ['parcel_notices', 'parcel_appeal_decisions', 'parcel_appeal_deadlines', 'parcel_non_ntrg'] },
  { key: 'bills', label: 'Tax bills', tables: ['tax_bills', 'tax_bill_parcels', 'tax_bill_references', 'parcel_taxes', 'parcel_collectors', 'tax_bill_transmittals'], entityTypes: ['tax_bill'] },
  { key: 'documents', label: 'Documents', tables: ['file_exchange', 'parcel_documents', 'document_requests', 'document_request_items', 'upload_tokens', 'deliveries', 'delivery_files'], entityTypes: ['file', 'file_exchange', 'parcel', 'property'] },
  { key: 'notes', label: 'Notes', tables: ['notes'], entityTypes: ['note'] },
  { key: 'records', label: 'Clients & parcels', tables: ['clients', 'properties', 'parcels', 'client_contacts', 'client_contact_roles', 'client_role_assignments', 'contact_properties', 'parcel_contact_roles', 'consultants', 'consultant_contacts', 'consultant_contact_roles', 'consultant_assignments', 'collectors', 'collector_entities', 'collector_installments', 'assessors', 'assessor_entities', 'assessor_collectors'] },
  { key: 'tables', label: 'Table Management', tables: ['doc_folders', 'doc_types', 'notice_types', 'level_names', 'lookup_lists', 'lookup_options', 'client_groups', 'client_group_members'] },
  { key: 'users', label: 'Users', tables: ['ntrg_staff', 'client_portal_users', 'client_access_properties', 'pto_coverage', 'task_coverage'] },
  { key: 'tasks', label: 'Tasks', tables: ['tasks', 'task_attachments'] },
  { key: 'questions', label: 'Open Questions', tables: ['open_questions', 'open_question_parcels'] },
];
export const SOURCES = [
  { key: 'app', label: 'Screens' }, { key: 'server', label: 'Server' }, { key: 'script', label: 'Scripts & imports' }, { key: 'sql', label: 'SQL editor' },
];

// What a table's row is, in words.
const TABLE_LABEL = {
  parcel_appeals: 'values', parcel_appeal_deductions: 'deduction', parcel_values: 'scanned values', parcel_prorata: 'pro-rata',
  parcel_jurisdiction_rates: 'tax rate', parcel_status: 'parcel status', parcel_year_data: 'appeal status',
  parcel_notices: 'notice', parcel_appeal_decisions: 'decision', parcel_appeal_deadlines: 'deadline', parcel_non_ntrg: 'Non-NTRG tick',
  tax_bills: 'tax bill', tax_bill_parcels: 'bill parcel link', tax_bill_references: 'reference bill', parcel_taxes: 'Taxes tab',
  parcel_collectors: 'collector link', tax_bill_transmittals: 'transmittal', file_exchange: 'document', parcel_documents: 'document link',
  document_requests: 'document request', document_request_items: 'requested document', upload_tokens: 'upload link', deliveries: 'delivery',
  delivery_files: 'delivered file', notes: 'note', clients: 'client', properties: 'property', parcels: 'parcel', client_contacts: 'client contact',
  client_contact_roles: 'contact role', client_role_assignments: 'client role', contact_properties: 'contact property', parcel_contact_roles: 'parcel contact',
  consultants: 'consultant', consultant_contacts: 'consultant contact', consultant_contact_roles: 'consultant contact role', consultant_assignments: 'consultant assignment',
  collectors: 'collector', collector_entities: 'collector', collector_installments: 'installment schedule', assessors: 'assessor', assessor_entities: 'assessor',
  assessor_collectors: 'assessor–collector link', doc_folders: 'folder', doc_types: 'document type', notice_types: 'notice type', level_names: 'level name',
  lookup_lists: 'pick list', lookup_options: 'pick-list option', client_groups: 'client group', client_group_members: 'client group member', ntrg_staff: 'staff record',
  client_portal_users: 'portal user', client_access_properties: 'portal access', pto_coverage: 'PTO coverage', task_coverage: 'task coverage', tasks: 'task', task_attachments: 'task attachment',
  open_questions: 'open question', open_question_parcels: 'question parcel',
};
const OP_VERB = { add: 'added', edit: 'changed', delete: 'deleted' };
// Events written by screens.
const ACTION_LABEL = {
  scan: 'scanned', file: 'filed', rename: 'renamed', dismiss: 'dismissed', submit: 'submitted a bill for', flag: 'flagged a bill for',
  reopen: 'reopened', mark_paid: 'marked paid', mark_unpaid: 'marked unpaid', file_without_scanning: 'filed without scanning',
  note_edit: 'edited a note', note_delete: 'deleted a note', upload: 'uploaded', delete: 'deleted', appeal_document: 'attached an appeal document',
};
// Columns a person needs no word on.
const QUIET = new Set(['id', 'created_at', 'updated_at', 'parcel_id', 'property_id', 'client_id', 'tax_year', 'notice_no']);

export const isChange = (r) => !!r?.table_name;
export const kindOf = (r) => KINDS.find((k) => (isChange(r) ? k.tables.includes(r.table_name) : (k.entityTypes || []).includes(r?.entity_type))) || null;

// PostgREST filter pieces for a kind: changes by table, events by entity_type. → an `.or()` string.
export function kindOrFilter(kindKey) {
  const k = KINDS.find((x) => x.key === kindKey);
  if (!k) return null;
  const parts = [`table_name.in.(${k.tables.join(',')})`];
  if ((k.entityTypes || []).length) parts.push(`and(table_name.is.null,entity_type.in.(${k.entityTypes.join(',')}))`);
  return parts.join(',');
}

const colLabel = (c) => c.replace(/_/g, ' ');
export function fmtValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return v.toLocaleString('en-US', { maximumFractionDigits: 6 });
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 80);
  const s = String(v);
  if (/^-?\d+(\.\d+)?$/.test(s) && s.length < 16) return Number(s).toLocaleString('en-US', { maximumFractionDigits: 6 });
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

// One row in words: { verb, what, changes: [{ column, from, to }] }. An edit lists its changed columns; an add or a
// delete lists the row's meaningful columns as "to" (add) or "from" (delete).
export function describe(r) {
  if (!isChange(r)) {
    return { verb: ACTION_LABEL[r.action] || (r.action || '').replace(/_/g, ' '), what: r.entity_name || r.entity_type || '', changes: [] };
  }
  const what = TABLE_LABEL[r.table_name] || r.table_name;
  const ch = r.changes && typeof r.changes === 'object' ? r.changes : {};
  const changes = Object.entries(ch).filter(([c]) => !QUIET.has(c)).map(([column, v]) => (
    r.op === 'edit' && Array.isArray(v) ? { column: colLabel(column), from: fmtValue(v[0]), to: fmtValue(v[1]) }
      : r.op === 'delete' ? { column: colLabel(column), from: fmtValue(v), to: null }
        : { column: colLabel(column), from: null, to: fmtValue(v) }))
    .filter((c) => !(c.from === '—' && c.to === null) && !(c.to === '—' && c.from === null));
  return { verb: OP_VERB[r.op] || r.op, what, changes };
}

// Who, as one key: the staff row when known (by login), else the name on the row, else where it came from.
export const actorKey = (r) => r.staff_id || (r.staff_user ? `name:${r.staff_user}` : `source:${r.source || 'unknown'}`);
const contextKey = (r) => (r.batch && r.source === 'script' ? `batch:${r.batch}` : `${r.parcel_id || r.property_id || r.client_id || r.entity_name || ''}|${r.tax_year || ''}`);

// ⛔ GROUPED FOR READING (decision: one person, one parcel-year, within 2 minutes = one entry; an import's rows = one
// entry). Rows arrive newest first; a row joins the group above it when the person and the parcel-year (or the import
// batch) match and it is within GROUP_MINUTES of that group's oldest row. Each group keeps its rows for expanding.
export const GROUP_MINUTES = 2;
export function groupEntries(rows) {
  const groups = [];
  (rows || []).forEach((r) => {
    const g = groups[groups.length - 1];
    const t = new Date(r.created_at).getTime();
    const sameBatch = g && r.batch && r.source === 'script' && g.key === `${actorKey(r)}#${contextKey(r)}`;
    if (g && g.key === `${actorKey(r)}#${contextKey(r)}` && (sameBatch || g.oldest - t <= GROUP_MINUTES * 60000)) {
      g.rows.push(r); g.oldest = t;
    } else {
      groups.push({ key: `${actorKey(r)}#${contextKey(r)}`, rows: [r], newest: t, oldest: t });
    }
  });
  return groups;
}

// ═══ WORK DONE, per person, over the rows given (Productivity) ═══
// Counted from CHANGES (the database's own record), so a screen that forgets to log can't hide work:
//   valuesYears   parcel-years whose values or deductions were added / changed
//   notices       notices added                 decisions  decisions set or changed
//   billsEntered  tax bills added (typed or scanned)        billsApproved  tax bills whose status became Approved
//   docsFiled     documents filed (filed_at set)            notes          notes added
//   recordsAdded  clients, properties and parcels added     changes        every change in the log
// → Map(actorKey → counts)
export function workDone(rows) {
  const out = new Map();
  const get = (r) => {
    const k = actorKey(r);
    if (!out.has(k)) out.set(k, { valuesYears: new Set(), notices: 0, decisions: 0, billsEntered: 0, billsApproved: 0, docsFiled: 0, notes: 0, recordsAdded: 0, changes: 0 });
    return out.get(k);
  };
  (rows || []).filter(isChange).forEach((r) => {
    const w = get(r);
    const ch = r.changes || {};
    w.changes += 1;
    if (['parcel_appeals', 'parcel_appeal_deductions'].includes(r.table_name) && r.op !== 'delete' && r.parcel_id) w.valuesYears.add(`${r.parcel_id}|${r.tax_year || ''}`);
    if (r.table_name === 'parcel_notices' && r.op === 'add') w.notices += 1;
    if (r.table_name === 'parcel_appeal_decisions' && r.op !== 'delete') w.decisions += 1;
    if (r.table_name === 'tax_bills' && r.op === 'add') w.billsEntered += 1;
    if (r.table_name === 'tax_bills' && r.op === 'edit' && Array.isArray(ch.status) && ch.status[1] === 'approved') w.billsApproved += 1;
    if (r.table_name === 'file_exchange' && r.op === 'edit' && Array.isArray(ch.filed_at) && !ch.filed_at[0] && ch.filed_at[1]) w.docsFiled += 1;
    if (r.table_name === 'notes' && r.op === 'add') w.notes += 1;
    if (['clients', 'properties', 'parcels'].includes(r.table_name) && r.op === 'add') w.recordsAdded += 1;
  });
  out.forEach((w) => { w.valuesYears = w.valuesYears.size; });
  return out;
}

// The middle of a list of durations (ms), or null.
export function median(values) {
  const v = (values || []).filter((x) => Number.isFinite(x) && x >= 0).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}
// A duration in words: "3.5 h" under two days, else "4.2 d".
export const fmtDuration = (ms) => (ms == null ? '—' : ms < 48 * 3600000 ? `${(ms / 3600000).toFixed(1)} h` : `${(ms / 86400000).toFixed(1)} d`);
